/**
 * GameEngine — renderer shell, warm-light stage (卷五 · 暖光律).
 * 4800K key light + cool-warm collision fill, ACES filmic tone mapping,
 * PMREM room environment for glass & brass, drifting dust motes,
 * and quality tiers from QUALITY_TIERS.
 * Scenes attach content; the engine owns light, air and time.
 */
import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import type { ThemePreset, QualityTier } from '@shared/config/theme';
import { CameraRig } from './CameraRig';
import { InputManager, type InputHandlers } from './InputManager';

/** Must stay identical to MarbleFactory LIGHT_DIR. */
const LIGHT_DIR = new THREE.Vector3(-0.42, 0.78, 0.46).normalize();

export type FrameCallback = (dt: number, elapsed: number) => void;

export class GameEngine {
  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private clock!: THREE.Clock;
  private rig!: CameraRig;
  private input!: InputManager;

  private keyLight!: THREE.DirectionalLight;
  private dust: {
    points: THREE.Points;
    speeds: Float32Array;
    update: (dt: number, elapsed: number) => void;
    dispose: () => void;
  } | null = null;

  private envTexture: THREE.Texture | null = null;
  private pmrem: THREE.PMREMGenerator | null = null;
  private frameCb: FrameCallback | null = null;
  private animationId = 0;
  private disposed = false;
  private resizeHandler = (): void => this.onResize();

  init(
    container: HTMLElement,
    tier: QualityTier,
    theme: ThemePreset,
    handlers: InputHandlers = {}
  ): void {
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, tier.pixelRatioCap));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.shadowMap.enabled = tier.shadows;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.12;
    // 渲染层独立铺满定位容器：canvas 永居底层，DOM UI（fixed + 更高 z-index）恒浮其上。
    // 避免 3D 画布在部分渲染管线/缩放下盖住 HUD、菜单等界面层。
    const cv = this.renderer.domElement;
    cv.style.position = 'absolute';
    cv.style.inset = '0';
    cv.style.width = '100%';
    cv.style.height = '100%';
    container.appendChild(cv);

    this.scene = new THREE.Scene();
    this.applyTheme(theme);

    this.camera = new THREE.PerspectiveCamera(42, window.innerWidth / window.innerHeight, 0.1, 120);
    this.rig = new CameraRig(this.camera);

    // ── 暖光律：4800K 主光 + 冷暖对撞补光 ──
    this.keyLight = new THREE.DirectionalLight('#FFD9AE', 2.6);
    this.keyLight.position.copy(LIGHT_DIR).multiplyScalar(30);
    this.keyLight.shadow.mapSize.set(tier.shadowMapSize, tier.shadowMapSize);
    this.keyLight.shadow.camera.near = 6;
    this.keyLight.shadow.camera.far = 70;
    const S = 15;
    this.keyLight.shadow.camera.left = -S;
    this.keyLight.shadow.camera.right = S;
    this.keyLight.shadow.camera.top = S;
    this.keyLight.shadow.camera.bottom = -S;
    this.keyLight.shadow.bias = -0.0004;
    this.keyLight.shadow.normalBias = 0.03;
    this.scene.add(this.keyLight);
    this.scene.add(this.keyLight.target);

    const fillLight = new THREE.DirectionalLight('#AFC8FF', 0.5); // 冷白补光
    fillLight.position.set(14, 9, -11);
    this.scene.add(fillLight);

    this.scene.add(new THREE.AmbientLight('#FFE2BD', 0.5));
    this.scene.add(new THREE.HemisphereLight('#FFF1D6', '#3A2A1A', 0.55));

    this.applyQuality(tier);

    this.clock = new THREE.Clock();
    window.addEventListener('resize', this.resizeHandler);

    this.input = new InputManager({
      dom: this.renderer.domElement,
      camera: this.camera,
      rig: this.rig,
      pickables: () => this.pickables,
      // Accessors, not snapshots: scenes replace the hole map / felt height on
      // every board rebuild via registerHoles(). Passing the values themselves
      // would freeze the initial empty registry inside InputManager and break
      // all hole picking (the P0 "can select, can never move" bug).
      holeWorld: () => this.holeWorld,
      feltY: () => this.feltY,
      handlers,
    });
    this.input.attach();

    this.loop();
  }

  // ── Stage registry for picking ──
  private pickables: THREE.Object3D[] = [];
  private holeWorld: Map<string, THREE.Vector3> = new Map();
  private feltY = 0.62;

  registerPickables(list: THREE.Object3D[]): void {
    this.pickables = list;
  }

  registerHoles(map: Map<string, THREE.Vector3>, feltY: number): void {
    this.holeWorld = map;
    this.feltY = feltY;
  }

  setFrameCallback(cb: FrameCallback | null): void {
    this.frameCb = cb;
  }

  getRig(): CameraRig {
    return this.rig;
  }

  getInput(): InputManager {
    return this.input;
  }

  getRenderer(): THREE.WebGLRenderer {
    return this.renderer;
  }

  getScene(): THREE.Scene {
    return this.scene;
  }

  getCamera(): THREE.PerspectiveCamera {
    return this.camera;
  }

  applyTheme(theme: ThemePreset): void {
    if (!this.scene) return;
    this.scene.background = new THREE.Color(theme.room);
    this.scene.fog = new THREE.FogExp2(theme.room, 0.011);
  }

  applyQuality(tier: QualityTier): void {
    if (!this.renderer) return;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, tier.pixelRatioCap));
    this.renderer.shadowMap.enabled = tier.shadows;
    this.keyLight.castShadow = tier.shadows;
    if (tier.shadows) this.keyLight.shadow.mapSize.set(tier.shadowMapSize, tier.shadowMapSize);
    this.keyLight.shadow.map?.dispose();
    this.keyLight.shadow.map = null;

    this.rebuildEnvironment(tier.envMap);
    this.rebuildDust(tier.dustParticles);
  }

  private rebuildEnvironment(enabled: boolean): void {
    if (this.envTexture) {
      this.scene.environment = null;
      this.envTexture.dispose();
      this.envTexture = null;
    }
    if (this.pmrem) {
      this.pmrem.dispose();
      this.pmrem = null;
    }
    if (!enabled) return;
    this.pmrem = new THREE.PMREMGenerator(this.renderer);
    const room = new RoomEnvironment();
    this.envTexture = this.pmrem.fromScene(room, 0.04).texture;
    this.scene.environment = this.envTexture;
    this.scene.environmentIntensity = 0.45;
    room.dispose?.();
  }

  // ── 尘埃粒子：暖房光柱里的浮尘（克制律：低存在感） ──
  private rebuildDust(count: number): void {
    if (this.dust) {
      this.scene.remove(this.dust.points);
      this.dust.dispose();
      this.dust = null;
    }
    if (count <= 0) return;

    const geo = new THREE.BufferGeometry();
    const pos = new Float32Array(count * 3);
    const speeds = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      const r = Math.sqrt(Math.random()) * 10.5;
      const a = Math.random() * Math.PI * 2;
      pos[i * 3] = Math.cos(a) * r;
      pos[i * 3 + 1] = 0.3 + Math.random() * 7;
      pos[i * 3 + 2] = Math.sin(a) * r;
      speeds[i] = 0.06 + Math.random() * 0.14;
    }
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));

    const sprite = makeDustSprite();
    const mat = new THREE.PointsMaterial({
      map: sprite,
      color: '#FFD9A0',
      size: 0.16,
      transparent: true,
      opacity: 0.4,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });
    const points = new THREE.Points(geo, mat);
    points.renderOrder = 2;
    this.scene.add(points);

    const update = (dt: number, elapsed: number): void => {
      const arr = geo.getAttribute('position') as THREE.BufferAttribute;
      const a = arr.array as Float32Array;
      for (let i = 0; i < count; i++) {
        const j = i * 3;
        const ny = (a[j + 1] ?? 0.3) + (speeds[i] ?? 0.1) * dt;
        a[j + 1] = ny > 7.6 ? 0.3 : ny;
        a[j] = (a[j] ?? 0) + Math.sin(elapsed * 0.4 + i) * 0.02 * dt;
      }
      arr.needsUpdate = true;
    };
    const dispose = (): void => {
      geo.dispose();
      mat.dispose();
      sprite.dispose();
    };
    this.dust = { points, speeds, update, dispose };
  }

  private loop = (): void => {
    if (this.disposed) return;
    this.animationId = requestAnimationFrame(this.loop);
    const dt = Math.min(this.clock.getDelta(), 0.1);
    const elapsed = this.clock.getElapsedTime();
    this.rig.update(dt);
    this.dust?.update(dt, elapsed);
    // A frame callback must never abort the render loop: one transient throw
    // would otherwise stop requestAnimationFrame forever and freeze the board.
    try {
      this.frameCb?.(dt, elapsed);
    } catch (err) {
      this.logFrameError(err);
    }
    this.renderer.render(this.scene, this.camera);
  };

  /** Log the first frame error and then throttle, so a recurring bug can't spam. */
  private lastFrameError = 0;
  private logFrameError(err: unknown): void {
    const now = performance.now();
    if (now - this.lastFrameError < 2000) return;
    this.lastFrameError = now;
    console.error('[GameEngine] frame callback threw — loop kept alive:', err);
  }

  private onResize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  }

  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.animationId);
    window.removeEventListener('resize', this.resizeHandler);
    this.input?.dispose();
    this.dust?.dispose();
    this.envTexture?.dispose();
    this.pmrem?.dispose();
    this.renderer?.dispose();
    this.renderer?.domElement.remove();
  }
}

/** Soft radial sprite for dust motes (zero external assets). */
function makeDustSprite(): THREE.CanvasTexture {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.4, 'rgba(255,255,255,0.45)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
  }
  return new THREE.CanvasTexture(canvas);
}
